import type { Group } from "@repo/app-types/entities/group";
import type { Teammate, TeammateId } from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { type FC, useEffect, useMemo, useState } from "react";
import {
  useCreateTeammateGroupMutation,
  useUpdateTeammateGroupMutation,
} from "../../../api/entities/group.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  fetchTeammates,
  selectAllTeammates,
} from "../../teammates/teammatesSlice.js";

interface TeammateGroupDialogProps {
  isOpen: boolean;
  group?: Group;
  onClose: () => void;
}

interface TeammateOption {
  id: TeammateId;
  name: string;
}

function teammateToOption(teammate: Teammate): TeammateOption {
  return {
    id: teammate.id,
    name: teammate.email,
  };
}

export const TeammateGroupDialog: FC<TeammateGroupDialogProps> = ({
  isOpen,
  group,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [createGroup, { isLoading: isCreating }] =
    useCreateTeammateGroupMutation();
  const [updateGroup, { isLoading: isUpdating }] =
    useUpdateTeammateGroupMutation();
  const [groupName, setGroupName] = useState("");
  const [selectedTeammateIds, setSelectedTeammateIds] = useState<
    readonly TeammateId[]
  >([]);

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!group;

  // Fetch teammates when dialog opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchTeammates());
    }
  }, [isOpen, dispatch]);

  const allTeammates = useAppSelector((state) =>
    selectAllTeammates(state.teammates),
  );

  // Selected teammates stay in the list (shown as selected) so a chip can
  // render for each and a mis-click can be undone from the menu.
  const teammateOptions = allTeammates.map(teammateToOption);

  useEffect(() => {
    if (group) {
      setGroupName(group.name);
      setSelectedTeammateIds(group.teammate_ids);
    } else {
      setGroupName("");
      setSelectedTeammateIds([]);
    }
  }, [group]);

  const closeAndReset = () => {
    setGroupName("");
    setSelectedTeammateIds([]);
    onClose();
  };

  const onSave = async () => {
    if (isEditMode && group) {
      await updateGroup({
        id: group.id,
        name: groupName,
        teammate_ids: selectedTeammateIds,
      }).unwrap();
    } else {
      await createGroup({
        name: groupName.trim(),
        teammate_ids: selectedTeammateIds,
      }).unwrap();
    }
    closeAndReset();
  };

  const selectedIds = useMemo(
    () => [...selectedTeammateIds],
    [selectedTeammateIds],
  );

  return (
    <Modal isOpen={isOpen} onCancel={closeAndReset} size={Size.LARGE}>
      <ModalHeader onClose={closeAndReset}>
        {isEditMode ? "Edit teammate group" : "Create teammate group"}
      </ModalHeader>
      <div className="mt-4 w-[720px] max-w-[calc(100vw-3rem)] space-y-4">
        <div className="w-[400px] max-w-full">
          <Input
            id="group-name"
            label="Name"
            placeholder="Teammate group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            inputClassName="w-full"
            maxLength={100}
          />
        </div>
        <div className="w-full">
          <ComboboxField<TeammateOption, "multiple">
            label="Teammates"
            selectionMode="multiple"
            items={teammateOptions}
            getLabel={(teammate) => teammate.name}
            value={selectedIds}
            onChange={setSelectedTeammateIds}
            placeholder="Search for a teammate to add to the group"
          />
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <Button variant={ButtonVariant.Secondary} onClick={closeAndReset}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={onSave}
            isLoading={isLoading}
            isDisabled={groupName.trim() === ""}
          >
            {isEditMode ? "Save changes" : "Create teammate group"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
